import {
  cloneElement,
  forwardRef,
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isWeb, useComposedRefs, View } from '@tamagui/core';
import type { TamaguiElement } from '@tamagui/core';
import { useInView } from 'react-intersection-observer';
import type { IntersectionOptions } from 'react-intersection-observer';
import type { View as NativeView } from 'react-native';
import { Dimensions } from 'react-native';

import { useObservabilitySafe } from './useObservabilitySafe';

/**
 * NOTE ABOUT METRIC TRACKING:
 *
 * Click events are tracked by default for all interactive components (e.g. Button, Link, etc.):
 * - This is why analyticsNames are required for interactive components
 *
 * Impression events are only tracked if:
 *  1) a non-empty analyticsName is passed down - Convention: {appName}-{textName}
 *  2) trackImpression is true  and text enters viewport (threshold is set at 100% for now.  threshold: Number between 0 and 1 indicates the percentage that should be visible before triggering.)
 *
 * Events will be sent to the journey service and will end up in the corresponding tables (e.g. journey_service_production.text_view_event for text impression events)
 * For more information on tracking please refer here: https://opendoor.atlassian.net/wiki/spaces/SELL/pages/1926201404/EDD+User+Behavior+Metrics+in+Bricks
 */
export interface AnalyticsProps {
  /**
   * Name of the component in analytics events, should be unique within the app
   * Convention: {appName}-{componentName}
   */
  analyticsName: string;
  /**
   * Whether to track an impression event when the component first enters the viewport
   */
  trackImpression?: boolean;
}

export type InViewEventType =
  | 'accordionViewEvent'
  | 'boxViewEvent'
  | 'buttonViewEvent'
  | 'cardViewEvent'
  | 'checkboxViewEvent'
  | 'imageViewEvent'
  | 'inputViewEvent'
  | 'linkViewEvent'
  | 'radioViewEvent'
  | 'textViewEvent';

type InViewRef = (node?: Element | null) => void;

/**
 * This hook is used to track component in view events. It is used to track the first time a component enters the viewport.
 * @param eventName - The event name to track
 * @param analyticsName - The name of the component to track
 * @returns A ref to be passed to the component which is used to trigger the inView event
 */
const useTrackComponentInViewForWeb = (
  eventName: InViewEventType,
  analyticsName: string,
  options?: IntersectionOptions,
): InViewRef => {
  const { trackJourneyEvent } = useObservabilitySafe();

  // Tracks impression events
  const [inViewRef, inView] = useInView({
    // https://react-intersection-observer.vercel.app/?path=/docs/useinview-hook--basic
    // threshold: Number between 0 and 1 indicates the percentage that should be visible
    // before triggering. (defaults to 100% unless an override is provided)
    threshold: 1,
    skip: !analyticsName,
    triggerOnce: true,
    ...options,
  });

  useEffect(() => {
    // this tracks impression events whenenver the component enters or leaves the viewport
    if (inView) {
      trackJourneyEvent('platform', eventName, { targetName: analyticsName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, analyticsName]);

  return useMemo(() => inViewRef, [inViewRef]);
};

type TrackingProps<T extends object> = { Component: JSX.Element } & T &
  Pick<AnalyticsProps, 'analyticsName'> & {
    eventName: InViewEventType;
    /**
     * In box users are able to override the default inViewThreshold
     * Threshold for impression tracking. 0.5 means 50% of the box must be in view to trigger impression
     * Default: 1 (100%)
     */
    inViewThreshold?: number;
  };

const WebComponentWithViewTracking = forwardRef(function WebComponentWithViewTracking<
  T extends object,
>(props: TrackingProps<T>, ref: React.Ref<HTMLElement>) {
  const { Component, analyticsName, eventName, inViewThreshold } = props;
  const inViewRef = useTrackComponentInViewForWeb(eventName, analyticsName, {
    threshold: inViewThreshold,
  });
  const ComponentWithViewTracking = cloneElement(Component, {
    ref: useComposedRefs(inViewRef, ref),
  });
  return ComponentWithViewTracking;
});

/**
 *
 * Solution inspired by code here: https://github.com/SvanBoxel/visibility-sensor-react-native/blob/master/src/index.tsx
 */
const MobileComponentWithViewTracking = forwardRef(function MobileComponentWithViewTracking<
  T extends object,
>(props: TrackingProps<T>, _ref: React.Ref<HTMLElement>) {
  const { Component, analyticsName, eventName } = props;
  let inViewThreshold = props.inViewThreshold || 1;
  const { trackJourneyEvent } = useObservabilitySafe();
  const inViewRef = useRef<NativeView>(null);
  const [trackedInView, setTrackedInView] = useState<boolean>(false);
  const interval = useRef<ReturnType<typeof setInterval> | null>(null);
  const startWatching = useCallback(() => {
    if (interval.current || trackedInView) {
      return;
    }

    interval.current = setInterval(() => {
      if (!inViewRef || !inViewRef.current) {
        return;
      }
      // gets the dimension of the component and determines if it is in view
      inViewRef.current?.measure(
        (_x: number, _y: number, width: number, height: number, pageX: number, pageY: number) => {
          // console.log('analyticsName', analyticsName)
          const window = Dimensions.get('window');
          if (height > window.height || width > window.width) {
            // auto updates inViewThreshold to ensure component will be tracked if it's larger than the window
            inViewThreshold =
              (height > window.height ? window.height / height : window.width / width) - 0.01;
          }
          const componentHeight = height * inViewThreshold;
          const componentWidth = width * inViewThreshold;
          const rectBottom = pageY + componentHeight;
          const rectTop = pageY;
          const rectRight = pageX + componentWidth;
          const isVisible =
            rectBottom !== 0 && // Check if the bottom edge is not at the top (initialized value)
            rectTop >= 0 && // Check if the top edge is greater than or equal to the top of the viewport
            rectBottom <= window.height && // Check if the bottom edge is within the inViewThreshold percentage of the viewport height
            rectRight >= 0 && // Check if the width is greater than 0 (initialized value)
            rectRight <= window.width; // Check if the width is within the inViewThreshold percentage of the viewport width
          if (trackedInView !== isVisible) {
            // only track the component the first time it enters the viewport
            setTrackedInView(isVisible);
            trackJourneyEvent('platform', eventName, { targetName: analyticsName });
          }
        },
      );
    }, 300);
  }, [trackedInView, inViewThreshold, trackJourneyEvent, eventName, analyticsName]);
  const stopWatching = useCallback(() => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }
  }, []);
  useEffect(() => {
    // We don't care about watching after the component has already been in view
    if (!trackedInView) {
      startWatching();
    }
    return stopWatching;
  }, [startWatching, stopWatching, trackedInView]);

  return (
    <View
      ref={inViewRef}
      // add collapsable-false to ensure view stays in the native view hierarchy for android
      // https://reactnative.dev/docs/view#collapsable-android
      collapsable={false}
    >
      {Component}
    </View>
  );
});

export const ComponentWithViewTracking = forwardRef(function ComponentWithViewTracking<
  T extends object,
>(props: TrackingProps<T>, ref: React.Ref<TamaguiElement>) {
  const { Component, analyticsName, eventName, inViewThreshold } = props;
  if (isWeb) {
    return (
      <WebComponentWithViewTracking
        Component={Component}
        analyticsName={analyticsName}
        eventName={eventName}
        inViewThreshold={inViewThreshold}
        ref={ref as React.Ref<HTMLElement>}
      />
    );
  }
  return (
    <MobileComponentWithViewTracking
      Component={Component}
      analyticsName={analyticsName}
      eventName={eventName}
      inViewThreshold={inViewThreshold}
      ref={ref as React.Ref<HTMLElement>}
    />
  );
});
